import { NavLink, useParams } from "react-router";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import {
  useHtzOfferShowQuery,
  useHtzOfferUpdateStateMutation,
} from "@/services/backend/htz/offers/api/offer-api";
import { Offer } from "@/services/backend/htz/offers/types/offer";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import t from "@/lang/lang";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { OfferPdf } from "@/routes/processes/[processId]/htz/offers/[offerId]/_components/offer-pdf";
import { DeleteOfferDialog } from "@/routes/processes/[processId]/htz/offers/[offerId]/_components/delete-offer-dialog";
import { ErrValidationFailedContextProvider } from "@/shared/lib/errors/err-validation-failed-context";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageNavigationOutlet,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { DocumentStateAlert } from "@/shared/bsm/core/document/components/ui/document-state-alert";
import { DocumentStateDropdown } from "@/shared/bsm/core/document/components/ui/document-state-dropdown";
import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";
import { ProcessPageMainTemplate } from "@/routes/processes/[processId]/_components/process-page-main-template";
import { HtzBreadcrumbs } from "@/routes/processes/[processId]/htz/_components/htz-breadcrumbs";
import { ReferenceNumber } from "@/services/backend/documents/components/reference-number";
import { CreateAhuDialog } from "@/services/backend/htz/ahu/components/create-ahu-dialog";
import { Button } from "@/shared/components/ui/button";
import { Plus } from "lucide-react";
import { CpyDialog } from "@/services/backend/cpy/components/cpy-dialog";
import { CpyName } from "@/services/backend/cpy/types/entity-names";

export function OfferRoute() {
  const { offerId } = useParams();

  const {
    data: offer,
    isLoading,
    error,
  } = useHtzOfferShowQuery({ id: offerId! });

  const breadcrumbs = offer && (
    <HtzBreadcrumbs customerId={offer.customerId} processId={offer.processId!}>
      <BreadcrumbSeparator />
      <BreadcrumbItem className="hidden md:block">
        <BreadcrumbLink asChild>
          <NavLink to={`/processes/htz/${offer.processId!}/offers`}>
            {t("Angebote")}
          </NavLink>
        </BreadcrumbLink>
      </BreadcrumbItem>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbPage>
          <ReferenceNumber referenceNumber={offer.referenceNumber} />
        </BreadcrumbPage>
      </BreadcrumbItem>
    </HtzBreadcrumbs>
  );

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <DocumentPageSkeleton />}
      {offer && (
        <PersistenceContextProvider>
          <ErrValidationFailedContextProvider>
            <OfferPage offer={offer} />
          </ErrValidationFailedContextProvider>
        </PersistenceContextProvider>
      )}
    </ProcessPageMainTemplate>
  );
}

function OfferPage({ offer }: { offer: Offer }) {
  const links = [
    {
      to: "data",
      label: t("Angebot"),
      fieldName: ["date", "referenceNumber", "specialAgreements"],
    },
    {
      to: "positions",
      label: t("Leistungen"),
      fieldName: ["positions"],
    },
    {
      to: "addresses-and-contacts",
      label: t("Adressen & Kontakte"),
      fieldName: ["postalAddress", "serviceAddress", "representative"],
    },
  ];

  return (
    <DocumentPage>
      <DocumentStateAlert document={offer} />
      <DocumentPageHeader>
        <DocumentPageNavigation>
          {links.map((link) => (
            <DocumentPageNavigationLink
              key={link.to}
              to={link.to}
              fieldName={link.fieldName}
            >
              {link.label}
            </DocumentPageNavigationLink>
          ))}
        </DocumentPageNavigation>
        <DocumentPageActions>
          {!isImmutable(offer) && (
            <>
              <PersistenceStateUsingContext className="mr-2" />
              <CpyDialog
                destinationEntityName={CpyName.HTZOffer}
                destinationId={offer.id}
                initialParams={{
                  // Default limit to results from same customer. 90% case.
                  customer: { active: true, values: [offer.customerId] },
                  orderBy: [{ field: "updatedAt", dsc: true }],
                }}
              />
              <CreateAhuDialog
                companyId={offer.companyId}
                customerId={offer.customerId}
                serviceObjectId={offer.serviceObjectId}
                trigger={
                  <Button variant="outline">
                    <Plus />
                    {t("RLT-Anlage erstellen")}
                  </Button>
                }
              />
              <DeleteOfferDialog offerId={offer.id} />
            </>
          )}
          <DocumentStateDropdown
            document={offer}
            mutation={useHtzOfferUpdateStateMutation}
          />
          <OfferPdf offer={offer} />
        </DocumentPageActions>
      </DocumentPageHeader>
      <DocumentPageContent>
        <DocumentPageNavigationOutlet />
      </DocumentPageContent>
    </DocumentPage>
  );
}
